"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth-context";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * Stealth landing.
 *
 * Replaced the consumer marketplace hero (vendor grid, category chips,
 * location search) on 2026-09-09 when the company moved off the events
 * positioning. This surface deliberately says almost nothing: a wordmark,
 * a badge, one headline and one line. No product, no vertical, no
 * geography, no signup.
 *
 * Everything the old landing linked to still exists and is reachable
 * directly (/browse, /explore, /signup, /vendor-tour) — it is only the
 * front door that went quiet.
 *
 * The sign-in affordance is intentionally understated: a small dot in the
 * leading top corner that names itself on hover, focus or tap. It is the
 * first thing in the tab order so keyboard and screen-reader users are
 * never locked out of the login route.
 *
 * The legal footer (privacy, terms, accessibility, company details) is
 * server-rendered in app/layout.tsx OUTSIDE this tree — do not duplicate
 * it here, and do not remove it there: Google's OAuth homepage check
 * fetches the page without JS and rejected us once already for it.
 */
export default function HomePage() {
  const router = useRouter();
  const { t } = useTranslation();
  usePageTitle(t("web.stealth.documentTitle", { defaultValue: "The Runup" }));
  const { isAuthenticated, isLoading, user } = useAuth();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    // Signed-in users never see the stealth page. Mid-onboarding users
    // continue their flow; finished users go to their main app. Vendors
    // route through /vendor — the vendor layout dispatches by real signup
    // state (business-details / verify / setup hub / dashboard).
    if (user?.hasCompletedOnboarding === false) {
      router.replace(user?.role === "vendor" ? "/vendor" : "/tour");
    } else if (user?.role === "vendor") {
      router.replace("/vendor");
    } else {
      router.replace("/feed");
    }
  }, [isAuthenticated, isLoading, user, router]);

  if (isLoading || isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-on-surface-variant">
            {isAuthenticated ? t("web.landing.redirecting") : t("web.landing.loading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[88svh] overflow-hidden bg-surface font-body text-on-surface">
      {/* Ambient brand bloom — the only decoration, standing in for the
          photography the marketplace hero used to carry. */}
      <div className="brand-bloom pointer-events-none absolute inset-0" aria-hidden="true" />

      {/* The quiet way in. Sits alone in the leading top corner. */}
      <div className="absolute top-3 z-30 start-3 sm:top-5 sm:start-5">
        <Link
          href="/login"
          aria-label={t("auth.signIn")}
          className="focus-ring-primary group flex h-11 items-center gap-2.5 rounded-full px-4 transition-all duration-300 hover:bg-surface-container-lowest hover:shadow-card"
        >
          <span
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-full bg-outline-variant transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:shadow-[0_0_0_4px_rgba(131,54,178,0.12)] group-focus-visible:bg-primary"
          />
          <span
            aria-hidden="true"
            className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold text-primary opacity-0 transition-all duration-300 group-hover:max-w-[7rem] group-hover:opacity-100 group-focus-visible:max-w-[7rem] group-focus-visible:opacity-100"
          >
            {t("auth.signIn")}
          </span>
        </Link>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[88svh] w-full max-w-screen-xl flex-col justify-center px-6 py-24 sm:px-10 lg:px-16">
        <div className="max-w-3xl">
          <p className="font-headline text-2xl font-bold tracking-tight text-primary">
            The Runup
          </p>

          <span className="mt-12 inline-block rounded-full bg-secondary-container/20 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-on-secondary-container sm:mt-20">
            {t("web.stealth.eyebrow")}
          </span>

          <h1 className="mt-5 max-w-[16ch] font-headline text-5xl font-extrabold leading-[1.08] tracking-tight text-on-surface sm:text-6xl lg:text-7xl">
            {t("web.stealth.headline")}{" "}
            <span className="bg-gradient-to-r from-primary to-primary-container bg-clip-text text-transparent">
              {t("web.stealth.headlineGradient")}
            </span>
          </h1>

          <p className="mt-7 max-w-[44ch] text-base leading-relaxed text-on-surface-variant sm:text-lg">
            {t("web.stealth.lede")}
          </p>
        </div>
      </div>
    </div>
  );
}
